import React, { useState } from 'react';
import { useTimezone, COMMON_TIMEZONES } from '../contexts/TimezoneContext';
import { Clock } from 'lucide-react';
import './UserPreferences.css';

function UserPreferences() {
  const { userTimezone, updateUserTimezone, activeTimezone, hasOverride, loadUserPreferences } = useTimezone();
  const [localTimezone, setLocalTimezone] = useState(userTimezone);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load preferences when component mounts
  React.useEffect(() => {
    let cancelled = false;
    const loadPrefs = async () => {
      setLoading(true);
      try {
        const prefs = await loadUserPreferences();
        if (!cancelled && prefs?.timezone) {
          setLocalTimezone(prefs.timezone);
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadPrefs();
    return () => {
      cancelled = true;
    };
  }, []); // Remove loadUserPreferences from dependencies to prevent infinite loop

  // Update local state when userTimezone changes
  React.useEffect(() => {
    setLocalTimezone(userTimezone);
  }, [userTimezone]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveStatus(null);

    try {
      await updateUserTimezone(localTimezone);
      setSaveStatus({ type: 'success', message: 'Timezone preference saved!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Failed to save timezone preference:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save timezone preference';
      setSaveStatus({ type: 'error', message: errorMessage });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="user-preferences">
      <div className="user-preferences-header">
        <Clock size={20} />
        <h2>User Preferences</h2>
      </div>

      <div className="user-preferences-content">
        <form onSubmit={handleSave} className="user-preferences-form">
          <div className="form-group">
            <label htmlFor="default-timezone" className="form-label">
              Default Timezone
            </label>
            <p className="form-description">
              Set your default timezone for displaying dates and times throughout the application.
              You can temporarily override this using the timezone selector in the footer.
            </p>
            <select
              id="default-timezone"
              value={localTimezone}
              onChange={(e) => setLocalTimezone(e.target.value)}
              className="form-select"
              disabled={saving || loading}
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            {hasOverride && (
              <p className="form-hint">
                <strong>Note:</strong> You currently have a temporary override active ({activeTimezone}).
                Changing your default timezone will clear the override.
              </p>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="form-submit-button"
              disabled={saving || loading || localTimezone === userTimezone}
            >
              {loading ? 'Loading...' : saving ? 'Saving...' : 'Save Timezone'}
            </button>
            {saveStatus && (
              <div className={`save-status ${saveStatus.type}`}>
                {saveStatus.message}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserPreferences;
